import { useState } from "react";
import { useTranslation } from "react-i18next";
import { apiFetchJson } from "../apiClient";
import { useAuth } from "../AuthContext";
import usePagedList from "../hooks/usePagedList";
import ApprovalPanel from "./ApprovalPanel";
import "./AdminCollections.css";

const INBOX_PATH = "/api/v1/agents/approvals";

/** Approvals inbox: every agent step and workflow question waiting for a decision the
 * current user may make (their own actions; for admins, others' second-approver actions). */
export default function ApprovalsInbox() {
  const { t } = useTranslation();
  const { user } = useAuth();
  const { items, hasMore, loading, error, loadMore, reload } = usePagedList(INBOX_PATH);
  const [reasons, setReasons] = useState({});
  const [busy, setBusy] = useState(false);
  const [actionError, setActionError] = useState("");

  const decide = async (run, verb) => {
    setBusy(true);
    try {
      await apiFetchJson(`/api/v1/agents/runs/${run.id}/${verb}`, {
        method: "POST", body: JSON.stringify({ reason: reasons[run.id] || "" }),
      });
      setActionError("");
    } catch (e) {
      setActionError(e.message);       // e.g. 409 "expired": the reload shows what is left to decide
    } finally {
      setBusy(false);
      await reload();
    }
  };

  return (
    <div className="ds-page">
      <header className="ds-page-header">
        <h2 className="ds-page-title">{t("agents.inboxTitle")}</h2>
        <button type="button" className="ds-btn ds-btn--secondary ds-btn--sm" disabled={loading}
                onClick={reload}>{t("agents.inboxRefresh")}</button>
      </header>
      {(error || actionError) && <div className="ds-alert ds-alert--danger" role="alert">{actionError || error}</div>}
      {!loading && items.length === 0 ? <div className="ds-empty">{t("agents.inboxEmpty")}</div> : (
        <ul className="ds-list">
          {items.map((run) => (
            <li key={run.id} className="ds-list-row">
              <div className="ds-list-detail">
                <p className="ds-meta">
                  {t("agents.inboxRequest", { agent: run.agent_name, user: run.user_email || run.user_id })}
                </p>
                <p className="ds-list-title">{run.task}</p>
                <ApprovalPanel run={run} user={user} busy={busy} reason={reasons[run.id]}
                               onReason={(value) => setReasons((p) => ({ ...p, [run.id]: value }))}
                               onDecide={(verb) => decide(run, verb)} />
              </div>
            </li>
          ))}
        </ul>
      )}
      {hasMore && (
        <div className="ac-actions">
          <button type="button" className="ds-btn ds-btn--secondary ds-btn--sm" disabled={loading} onClick={loadMore}>
            {t("agents.loadMore")}
          </button>
        </div>
      )}
    </div>
  );
}
